import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { AngebotStatusLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import { Angebot } from 'api/angebot';
import { Timestamps } from 'formComponents/Timestamps';
import { AngebotPosition } from 'api/angebotPosition';
import { Termin } from 'api/termin';
import {
    betreuerByNrSelector,
    skontodefinitionenByArtSelector,
    textbausteineByKzNrSelector,
} from 'store/lookups/selector';
import { formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { authSelector } from 'store/auth/selector';
import nl2br from 'react-nl2br';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { AngebotPositionen } from './AngebotPositionen';

const styles = mergeStyleSets({
    shortLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '120px 120px 1fr 1fr  100px max-content',
        gridColumnGap: 10,
    },
    status: {
        textAlign: 'right',
    },
});

interface AngebotItemProps {
    angebot: Angebot;
    angebotPositionen: AngebotPosition[];
    wiedervorlagen: Termin[];
    withKunde?: boolean;
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    editAngebot: () => void;
    refetch: () => void;
}

export const AngebotItem: React.FC<AngebotItemProps> = ({
    angebot,
    angebotPositionen,
    wiedervorlagen,
    withKunde,
    viewMode,
    withHeader = true,
    editAngebot,
    refetch,
}) => {
    const { kunde } = angebot;

    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const textbausteineByKzNr = useSelector(textbausteineByKzNrSelector);
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    const history = useHistory();

    if (!kunde) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={angebot.anlageDat}
            createdDecimalTime={angebot.anlageUhr}
            createdUser={angebot.anlageUser}
            modifiedDate={angebot.updDat}
            modifiedDecimalTime={angebot.updUhr}
            modifiedUser={angebot.updUser}
        />
    );

    const renderStatus = () => <div className={styles.status}>{AngebotStatusLabel[angebot.status]}</div>;

    const renderEditButton = () => (
        <div>
            {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editAngebot();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const containerClassName = classnames(viewMode === 'short' ? formStyles.listItemShort : formStyles.listItem, {
        [formStyles.angebotInArbeit]: angebot.status === 0,
        [formStyles.angebotVersendet]: angebot.status === 1,
        [formStyles.angebotAngenommen]: angebot.status === 2,
    });

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                <div className={styles.shortLine}>
                    <div className={formStyles.shortValue}>
                        <strong>{angebot.nr}</strong>
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{formatSmDate(angebot.datum)}</strong>
                    </div>
                    {withKunde ? (
                        <div
                            className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                            }}
                        >
                            {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    ) : (
                        <div />
                    )}

                    <div className={formStyles.shortValue}>
                        <strong>{angebot.aktText}</strong>
                    </div>

                    {renderStatus()}
                    {renderEditButton()}
                </div>

                <div>
                    <AngebotPositionen
                        kunde={kunde}
                        angebot={angebot}
                        aNr={angebot.nr}
                        angebotPositionen={angebotPositionen}
                        wiedervorlagen={wiedervorlagen}
                        viewMode={viewMode}
                        withHeader={withHeader}
                        refetch={refetch}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={containerClassName}>
                <div className={formStyles.topBlock}>
                    <div>
                        <div
                            className={classnames(formStyles.listTitle, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                                // openDetails();
                            }}
                        >
                            Angebot {angebot.nr} - {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.line}>
                            {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ansprechpartner:</div>
                            <div className={formStyles.dataValue}>{angebot.kaName}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Angebot-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{angebot.nr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{angebot.auNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ausstellungsdat.:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(angebot.datum)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Angebot gültig bis:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(angebot.datBis)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {angebot.betreuer &&
                                        (betreuerByNr[angebot.betreuer]
                                            ? betreuerByNr[angebot.betreuer].name || angebot.betreuer
                                            : angebot.betreuer)}
                                </strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Tel.-Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {angebot.telBetreuer
                                        ? betreuerByNr[angebot.telBetreuer]
                                            ? betreuerByNr[angebot.telBetreuer].name || angebot.telBetreuer
                                            : angebot.telBetreuer
                                        : '-'}
                                </strong>
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreff E-Akt:</div>
                            <div className={formStyles.dataValue}>{angebot.aktText}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Text:</div>
                            <div className={formStyles.dataValue}>{nl2br(angebot.txt)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 1:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.txtB1
                                    ? textbausteineByKzNr[21][angebot.txtB1]
                                        ? textbausteineByKzNr[21][angebot.txtB1].name || angebot.txtB1
                                        : angebot.txtB1
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 2:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.txtB2
                                    ? textbausteineByKzNr[21][angebot.txtB2]
                                        ? textbausteineByKzNr[21][angebot.txtB2].name || angebot.txtB2
                                        : angebot.txtB2
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 3:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.txtB3
                                    ? textbausteineByKzNr[21][angebot.txtB3]
                                        ? textbausteineByKzNr[21][angebot.txtB3].name || angebot.txtB3
                                        : angebot.txtB3
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 4:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.txtB4
                                    ? textbausteineByKzNr[21][angebot.txtB4]
                                        ? textbausteineByKzNr[21][angebot.txtB4].name || angebot.txtB4
                                        : angebot.txtB4
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 5:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.txtB5
                                    ? textbausteineByKzNr[21][angebot.txtB5]
                                        ? textbausteineByKzNr[21][angebot.txtB5].name || angebot.txtB5
                                        : angebot.txtB5
                                    : '-'}
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ext. Doku:</div>
                            <div className={formStyles.dataValue}>{angebot.extDoku ? 'Ja' : 'Nein'}</div>
                        </div>
                        {/*
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Teilrechnung:</div>
                            <div className={formStyles.dataValue}>{angebot.teilR ? 'Ja' : 'Nein'}</div>
                        </div>
                        */}
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferbed.:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.liefBed
                                    ? textbausteineByKzNr[11][angebot.liefBed]
                                        ? textbausteineByKzNr[11][angebot.liefBed].name || angebot.liefBed
                                        : angebot.liefBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferzeit:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.liefZeit
                                    ? textbausteineByKzNr[14][angebot.liefZeit]
                                        ? textbausteineByKzNr[14][angebot.liefZeit].name || angebot.liefZeit
                                        : angebot.liefZeit
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferdatum.:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.liefDat && (formatSmDate(angebot.liefDat, true) || '-')}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Liefer-KW.:</div>
                            <div className={formStyles.dataValue}>{angebot.liefKw ? angebot.liefKw : '-'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zahlungsbed.:</div>
                            <div className={formStyles.dataValue}>
                                {angebot.zahlBed
                                    ? skontodefinitionenByArt[angebot.zahlBed]
                                        ? skontodefinitionenByArt[angebot.zahlBed].text || angebot.zahlBed
                                        : angebot.zahlBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>MwSt:</div>
                            <div className={formStyles.dataValue}>{formatPercent(angebot.mwstCode)}</div>
                        </div>
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Bemerkungen (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(angebot?.kunde?.bemerkung)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Lieferbemerkung (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(angebot?.kunde?.liefBem)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Zusatzvereinbarung:</div>
                            <div className={formStyles.dataValue}>{nl2br(angebot?.zusatz)}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <AngebotPositionen
                        kunde={kunde}
                        angebot={angebot}
                        aNr={angebot.nr}
                        angebotPositionen={angebotPositionen}
                        wiedervorlagen={wiedervorlagen}
                        viewMode={viewMode}
                        refetch={refetch}
                    />
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.sumLine}>Summe Netto: {formatMoney(angebot.sNetto)}</div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.sumLine}>Summe Brutto: {formatMoney(angebot.sBrutto)}</div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.sumLine}>Summe MwSt.: {formatMoney(angebot.sMwst)}</div>
                    </div>
                </div>
                {renderTimestamps()}
            </div>
        </>
    );
};
